import { Component, computed, DestroyRef, ElementRef, HostListener, inject, Input, OnDestroy, OnInit, signal, ViewChild } from '@angular/core'
import { NgClass } from '@angular/common'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { catchError, EMPTY, Observable, Subject, forkJoin, finalize, mapTo, of, switchMap, tap } from 'rxjs'
import { CdkDrag, CdkDragDrop, CdkDragMove, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { DeleteModalComponent } from '@app/v2/shared/components/upload/components/delete-modal/delete-modal.component'
import { ProgressCircleComponent } from '@app/v2/shared/components/progress-circle/progress-circle.component'
import { ZoneService } from '../../services/zone.service'
import { Zone, ZoneCardErrors, ZoneResponse } from '../../types/zone.type'
import { ZoneCardComponent } from './zone-card/zone-card.component'

@Component({
  selector: 'app-zones',
  standalone: true,
  imports: [ZoneCardComponent, DeleteModalComponent, ProgressCircleComponent, DragDropModule, NgClass],
  templateUrl: './zones.component.html',
  styleUrl: './zones.component.scss',
  providers: [ZoneService]
})
export class ZonesComponent implements OnInit, OnDestroy {
  @Input() public eventId!: number
  @ViewChild('loadMoreTrigger')
  set loadMoreTriggerRef(el: ElementRef<HTMLDivElement> | undefined) {
    if (!el) return
    this._observer.disconnect()
    this._observer.observe(el.nativeElement)
  }

  public readonly zones = signal<Zone[]>([])
  public readonly isLoading = signal(false)
  public readonly isSaving = signal(false)
  public readonly zoneErrors = signal<Map<number, ZoneCardErrors>>(new Map())
  public readonly pendingDeletion = signal<Zone | null>(null)
  public readonly draggingZoneId = signal<number | null>(null)
  public readonly activeDraftZoneId = signal<number | null>(null)
  public readonly focusedZoneId = signal<number | null>(null)

  public readonly hasDrafts = computed(() => this._zoneAnalysis().drafts.length > 0)
  public readonly dirtyEdits = computed<Zone[]>(() => this._zoneAnalysis().dirty)
  public readonly hasPendingChanges = computed(() => {
    const { drafts, dirty } = this._zoneAnalysis()
    return drafts.length > 0 || dirty.length > 0
  })
  public readonly hasUnsavedChanges = this.hasPendingChanges

  public readonly deleteModalId: string = crypto.randomUUID()
  public readonly deleteZoneConfirmMessage = computed(() => {
    return `Are you sure you want to delete the zone <strong>${this.pendingDeletion()?.name || ''}</strong>?`
  })

  private readonly _zoneAnalysis = computed(() => {
    const snapshot = this._savedSnapshot()
    const drafts: Zone[] = []
    const dirty: Zone[] = []

    for (const zone of this.zones()) {
      if (this._isDraft(zone)) {
        drafts.push(zone)
        continue
      }

      if (zone.id == null) continue
      const saved = snapshot.get(zone.id)
      if (!saved) continue
      if (saved.name !== zone.name || saved.max_capacity !== zone.max_capacity || saved.weight !== zone.weight) dirty.push(zone)
    }

    return { drafts, dirty }
  })
  private readonly _destroyRef = inject(DestroyRef)
  private readonly _zoneService = inject(ZoneService)
  private readonly _authService = inject(AuthService)
  private readonly _alertService = inject(AlertService)
  private readonly _modalService = inject(ModalService)
  private readonly _savedSnapshot = signal<Map<number, Zone>>(new Map())
  private readonly _load$ = new Subject<boolean>() // true = reset
  private _page = 1
  private _hasMore = true
  private readonly _perPage = 10
  private _hasUserScrolled = false
  private _draftCounter = 0
  private readonly _dragAutoScrollEdgePx = 64
  private readonly _dragAutoScrollStepPx = 8
  private readonly _dragAutoScrollIntervalMs = 32
  private _activeScrollContainer: HTMLElement | Window = window
  private _dragSyncRafId: number | null = null
  private _lastAutoScrollAt = 0
  private readonly _isTouchDevice = window.matchMedia('(hover: none), (pointer: coarse)').matches
  private readonly _observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting && this._hasUserScrolled && this._hasMore && !this.isLoading()) this._load$.next(false)
    },
    { rootMargin: '200px', threshold: 0.01 }
  )

  @HostListener('window:wheel')
  @HostListener('window:touchmove')
  @HostListener('document:scroll')
  public onUserScrolled(): void {
    this._hasUserScrolled = true
  }

  /**
   * Whether the device is a touch device.
   * @returns {boolean}
   */
  public get isTouchDevice(): boolean {
    return this._isTouchDevice
  }

  ngOnInit(): void {
    this._initPaginatedLoading()
    this._load$.next(true)
  }

  ngOnDestroy(): void {
    this._observer.disconnect()
    if (this._dragSyncRafId != null) cancelAnimationFrame(this._dragSyncRafId)
    this._setGlobalDragCursor(false)
  }

  /**
   * Initializes the paginated loading of zones.
   */
  private _initPaginatedLoading(): void {
    this._load$
      .pipe(
        tap((reset) => this._onLoadRequested(reset)),
        switchMap(() => {
          if (!this._hasMore) {
            this.isLoading.set(false)
            return EMPTY
          }
          return this._zoneService.getZones(this._authService.currentWorkspace.id, this.eventId, this._perPage, this._page)
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (response) => this._onPageLoaded(response?.data ?? []),
        error: (error) => {
          this._hasMore = false
          this.isLoading.set(false)
          this._alertService.alert(Alert.ERROR, error?.message)
        }
      })
  }

  /**
   * Append a new draft zone card to the list. Drafts use a unique negative id so the rest of the
   * component can identify them (filter, track, update, delete) before they're persisted on save.
   */
  public createZone(): void {
    if (this.activeDraftZoneId() != null) return
    this._draftCounter -= 1
    const draft: Zone = {
      id: this._draftCounter,
      name: '',
      max_capacity: null,
      weight: this._getNextWeight()
    }
    this.zones.update((zones) => [...zones, draft])
    this.activeDraftZoneId.set(draft.id)
  }

  /**
   * Remove a zone. Drafts (negative/missing id) are removed locally without confirmation; persisted
   * zones open a confirmation modal first and are only deleted via the API once the user confirms.
   */
  public onZoneDeleted(zone: Zone): void {
    if (zone.id == null || zone.id <= 0) {
      this._removeZoneFromLocalState(zone.id)
      return
    }

    this.pendingDeletion.set(zone)
    if (!this._modalService.isModalOpen(this.deleteModalId)) this._modalService.toggleModal(this.deleteModalId)
  }

  /**
   * Confirm deletion of the pending zone. Calls the delete API and only removes the row on success.
   * Errors surface via an alert and leave the list untouched.
   */
  public confirmZoneDeletion(): void {
    const zone = this.pendingDeletion()
    if (!zone || zone.id == null) return

    this._zoneService
      .deleteZone(this._authService.currentWorkspace.id, this.eventId, zone.id)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this._removeZoneFromLocalState(zone.id)
          this._alertService.alert(Alert.SUCCESS, 'Zone deleted successfully')
        },
        error: (error) => {
          this._alertService.alert(Alert.ERROR, error?.error?.message || error?.message || 'Failed to delete zone')
        }
      })

    this.pendingDeletion.set(null)
  }

  /**
   * Cancel deletion: clear pending state and close the confirmation modal if still open.
   */
  public cancelZoneDeletion(): void {
    this.pendingDeletion.set(null)
    if (this._modalService.isModalOpen(this.deleteModalId)) this._modalService.toggleModal(this.deleteModalId)
  }

  /**
   * Whether a modal is currently open. Exposed for the template's `@if` guards.
   */
  public isModalOpened(modalId: string): boolean {
    return this._modalService.isModalOpen(modalId)
  }

  /**
   * Update zone name locally. Hook into update API once available.
   */
  public onZoneNameChanged(payload: { zone: Zone; name: string }): void {
    this._patchZone(payload.zone.id, { name: payload.name })
  }

  /**
   * Update zone capacity locally. Hook into update API once available.
   */
  public onZoneCapacityChanged(payload: { zone: Zone; max_capacity: number | null }): void {
    this._patchZone(payload.zone.id, { max_capacity: payload.max_capacity })
  }

  public onDraftNameBlur(payload: { zone: Zone; name: string }): void {
    if (payload.zone.id == null || payload.zone.id <= 0) {
      if (this.activeDraftZoneId() !== payload.zone.id) return
      if (payload.name.trim().length > 0) this.activeDraftZoneId.set(null)
    }
  }
  /**
   * Update the focused zone id.
   * @param payload { zone: Zone; focused: boolean }
   * @returns void
   */
  public onZoneFocusChanged(payload: { zone: Zone; focused: boolean }): void {
    if (payload.zone.id == null) return
    this.focusedZoneId.set(payload.focused ? payload.zone.id : null)
  }

  /**
   * Single Save action. Differentiates by id: zones without a positive id are created (bulk POST);
   * persisted zones with local edits are updated together (bulk PATCH). Both flows can run together.
   */
  public saveZones(): void {
    this.saveUnsavedChanges().pipe(takeUntilDestroyed(this._destroyRef)).subscribe()
  }

  /**
   * Reorders zone cards via drag and drop. Updates local list order and normalizes each item's
   * weight (1..n) so the existing dirty-check + save flow picks the change up.
   */
  public onDrop(event: CdkDragDrop<Zone[]>): void {
    if (event.previousIndex === event.currentIndex || this.isLoading()) return
    this.zones.update((zones) => {
      const next = [...zones]
      moveItemInArray(next, event.previousIndex, event.currentIndex)
      return this._recomputeWeights(next)
    })
  }

  /**
   * Toggle the dragging state of a zone.
   * @param {number | undefined} zoneId - The id of the zone to toggle the dragging state of.
   * @param {boolean} isDragging - Whether the zone is being dragged.
   */
  public toggleDragging(zoneId: number | undefined, isDragging: boolean): void {
    if (zoneId == null) return
    this.draggingZoneId.set(isDragging ? zoneId : null)
  }

  /**
   * Start dragging a zone.
   * @param {number | undefined} zoneId - The id of the zone to start dragging.
   * @returns {void}
   */
  public onCardMouseDown(zoneId: number | undefined): void {
    if (this._isTouchDevice) return
    this.toggleDragging(zoneId, true)
  }

  /**
   * Stop dragging a zone.
   * @param {number | undefined} zoneId - The id of the zone to stop dragging.
   * @returns {void}
   */
  public onCardMouseUp(zoneId: number | undefined): void {
    if (this._isTouchDevice) return
    this.toggleDragging(zoneId, false)
  }

  /**
   * Resolve the effective scroll container at drag start.
   * In wizard/modal overlays the scroller is often a parent element, not window.
   */
  public onDragStarted(dragRef: CdkDrag<Zone>): void {
    this._activeScrollContainer = this._resolveScrollContainer(dragRef.element.nativeElement, true)
    this._setGlobalDragCursor(true)
  }

  public onDragEnded(): void {
    this._setGlobalDragCursor(false)
  }

  /**
   * While dragging, auto-scroll the active scroll container when pointer nears its edges.
   * Supports both window scrolling and nested overflow containers.
   */
  public onDragMoved(event: CdkDragMove): void {
    const now = performance.now()
    if (now - this._lastAutoScrollAt < this._dragAutoScrollIntervalMs) return

    // Re-resolve during drag in case the start container detection missed the true scroller.
    if (this._activeScrollContainer === window) {
      this._activeScrollContainer = this._resolveScrollContainer(event.source.element.nativeElement, true)
    }

    const pointerY = event.pointerPosition.y
    const pointerX = event.pointerPosition.x
    const delta = this._getAutoScrollDelta(pointerY)
    if (delta === 0) return
    let didScroll = this._scrollActiveContainer(delta)
    if (!didScroll) {
      this._activeScrollContainer = this._resolveScrollContainer(event.source.element.nativeElement, false)
      didScroll = this._scrollActiveContainer(delta)
    }
    if (!didScroll) return
    this._syncDragAfterAutoScroll(pointerX, pointerY)
    this._lastAutoScrollAt = now
  }

  /**
   * Whether a zone can be dragged.
   * @param {number | undefined} zoneId - The id of the zone to check.
   * @returns {boolean}
   */
  public canDragZone(zoneId: number | undefined): boolean {
    if (zoneId == null || this.isLoading() || this.focusedZoneId() === zoneId || this.activeDraftZoneId() === zoneId) return false
    return true
  }

  /**
   * Whether a drag indicator should be shown for a zone.
   * @param {number | undefined} zoneId - The id of the zone to check.
   * @returns {boolean}
   */
  public showDragIndicator(zoneId: number | undefined): boolean {
    if (zoneId == null) return false
    if (!this._isTouchDevice) return false
    return this.draggingZoneId() === zoneId
  }

  /**
   * Wizard contract: triggered from the unsaved-changes modal. Persists drafts and dirty edits
   * in parallel and resolves once both calls settle so the wizard can close right after.
   */
  public saveUnsavedChanges(): Observable<void> {
    if (this.isSaving()) return of(void 0)
    const { drafts, dirty } = this._zoneAnalysis()
    if (!drafts.length && !dirty.length) return of(void 0)

    const tasks: Observable<unknown>[] = []
    if (drafts.length) tasks.push(this._createDrafts(drafts))
    if (dirty.length) tasks.push(this._updateZones(dirty))

    this.isSaving.set(true)
    return forkJoin(tasks).pipe(
      mapTo(void 0),
      finalize(() => this.isSaving.set(false))
    )
  }

  /**
   * Wizard contract: discard local edits when the user picks "Discard changes". Drops any draft
   * zones and resets touched persisted zones back to the last server snapshot.
   */
  public revertToSaved(): void {
    const snapshot = this._savedSnapshot()
    this.zones.update((zones) => {
      const next: Zone[] = []
      for (const zone of zones) {
        if (this._isDraft(zone)) continue
        if (zone.id != null && snapshot.has(zone.id)) {
          next.push(snapshot.get(zone.id) as Zone)
        } else {
          next.push(zone)
        }
      }
      return next
    })
    this.zoneErrors.set(new Map())
  }

  /**
   * A zone is a draft (create mode) when it has no positive id assigned by the server.
   */
  private _isDraft(zone: Zone): boolean {
    return zone.id == null || zone.id <= 0
  }

  /**
   * Append a paginated server page while keeping local drafts at the end. Server zones are deduped
   * by id and used to refresh the authoritative snapshot for dirty-edit comparison.
   */
  private _mergePageFromServer(pageData: Zone[]): void {
    const drafts: Zone[] = []
    const persistedById = new Map<number, Zone>()

    for (const zone of this.zones()) {
      if (this._isDraft(zone)) {
        drafts.push(zone)
        continue
      }
      this._setPersisted(persistedById, zone)
    }
    this._setPersistedMany(persistedById, pageData)

    this.zones.set([...Array.from(persistedById.values()), ...drafts])
    this._savedSnapshot.update((map) => {
      const next = new Map(map)
      this._setPersistedMany(next, pageData)
      return next
    })
  }

  /**
   * Bulk-create the given drafts. Returns an observable that completes once side effects run so
   * the unsaved-changes flow can await it via `firstValueFrom`.
   * @param {Zone[]} drafts
   */
  private _createDrafts(drafts: Zone[]): Observable<ZoneResponse> {
    const payload = drafts.map(({ name, max_capacity, weight }) => ({ name, max_capacity, weight }))

    return this._zoneService.createZones(this._authService.currentWorkspace.id, this.eventId, payload).pipe(
      takeUntilDestroyed(this._destroyRef),
      tap((response) => {
        const serverList = response?.data ?? []
        const dirtyById = this._asPersistedMap(this._zoneAnalysis().dirty)
        const merged = serverList.map((zone) => (zone.id != null && dirtyById.has(zone.id) ? (dirtyById.get(zone.id) as Zone) : zone))
        this.zones.set(merged)
        this._savedSnapshot.set(this._asPersistedMap(serverList))
        this.zoneErrors.set(new Map())
        this._alertService.alert(Alert.SUCCESS, response?.message || 'Zones created successfully')
      }),
      catchError((error) => {
        const fieldErrors = this._extractZoneFieldErrors(error, drafts)
        this.zoneErrors.set(fieldErrors)
        return EMPTY
      })
    )
  }

  /**
   * Bulk-PATCH all dirty zones in one request. On success, the response's authoritative zones are
   * merged back into both `zones` and `_savedSnapshot` so the entries drop out of `dirtyEdits`.
   * Server validation errors come back keyed as `zones.<i>.<field>`, indexed against `dirty`.
   */
  private _updateZones(dirty: Zone[]): Observable<ZoneResponse> {
    const payload = dirty
      .filter((zone): zone is Zone & { id: number } => zone.id != null && zone.id > 0)
      .map(({ id, name, max_capacity, weight }) => ({ id, name, max_capacity, weight }))
    if (!payload.length) return EMPTY

    return this._zoneService.updateZones(this._authService.currentWorkspace.id, this.eventId, payload).pipe(
      takeUntilDestroyed(this._destroyRef),
      tap((response) => {
        const updatedById = new Map<number, Zone>()
        for (const zone of response?.data ?? []) {
          if (zone.id != null) updatedById.set(zone.id, zone)
        }
        this.zones.update((zones) => zones.map((current) => (current.id != null && updatedById.has(current.id) ? { ...current, ...updatedById.get(current.id) } : current)))
        this._savedSnapshot.update((map) => {
          const next = new Map(map)
          updatedById.forEach((zone, id) => next.set(id, zone))
          return next
        })
        updatedById.forEach((_zone, id) => this._clearZoneErrors(id))
        this._alertService.alert(Alert.SUCCESS, response?.message || 'Zones updated successfully')
      }),
      catchError((error) => {
        const fieldErrors = this._extractZoneFieldErrors(error, dirty)
        if (fieldErrors.size) {
          this.zoneErrors.update((map) => {
            const next = new Map(map)
            fieldErrors.forEach((errs, id) => next.set(id, errs))
            return next
          })
        }
        const message = fieldErrors.size
          ? error?.error?.message || 'Some zones could not be updated. Please review the errors.'
          : error?.error?.message || error?.message || 'Failed to update zones'
        this._alertService.alert(Alert.ERROR, message)
        return EMPTY
      })
    )
  }

  /**
   * Drop any cached server errors for a specific zone after a successful update.
   */
  private _clearZoneErrors(zoneId: number): void {
    if (!this.zoneErrors().has(zoneId)) return
    this.zoneErrors.update((map) => {
      const next = new Map(map)
      next.delete(zoneId)
      return next
    })
  }

  /**
   * Extracts the field errors from the body
   * @param {unknown} error
   * @param {Zone[]} sent
   * @returns {Map<number, ZoneCardErrors>}
   */
  private _extractZoneFieldErrors(error: unknown, sent: Zone[]): Map<number, ZoneCardErrors> {
    const result = new Map<number, ZoneCardErrors>()
    const body = (error as { error?: Record<string, unknown> })?.error ?? (error as Record<string, unknown>)
    if (!body || typeof body !== 'object') return result

    for (const [key, value] of Object.entries(body)) {
      const match = /^zones\.(\d+)\.(name|max_capacity)$/.exec(key)
      if (!match) continue
      const zone = sent[Number(match[1])]
      if (!zone || zone.id == null) continue
      const field = match[2] as keyof ZoneCardErrors
      const label = field === 'name' ? 'Zone name' : 'Max capacity'
      const raw = Array.isArray(value) ? String(value[0]) : String(value)
      const current = result.get(zone.id) ?? {}
      current[field] = raw.split(key).join(label)
      result.set(zone.id, current)
    }
    return result
  }

  /**
   * Patches a zone in the local state.
   * @param {number | undefined} zoneId - The id of the zone to patch.
   * @param {Partial<Pick<Zone, 'name' | 'max_capacity'>>} patch - The patch to apply to the zone.
   */
  private _patchZone(zoneId: number | undefined, patch: Partial<Pick<Zone, 'name' | 'max_capacity'>>): void {
    if (zoneId == null) return
    this.zones.update((zones) => zones.map((current) => (current.id === zoneId ? { ...current, ...patch } : current)))
  }

  /**
   * Removes a zone from the local state.
   * @param {number | undefined} zoneId - The id of the zone to remove.
   */
  private _removeZoneFromLocalState(zoneId: number | undefined): void {
    if (zoneId == null) return
    if (this.activeDraftZoneId() === zoneId) this.activeDraftZoneId.set(null)
    if (this.focusedZoneId() === zoneId) this.focusedZoneId.set(null)
    this.zones.update((zones) => zones.filter((current) => current.id !== zoneId))
    this._deleteByKey(this._savedSnapshot, zoneId)
    this._deleteByKey(this.zoneErrors, zoneId)
  }

  /**
   * Called when a load request is made.
   * @param {boolean} reset - Whether to reset the load request.
   */
  private _onLoadRequested(reset: boolean): void {
    if (reset) {
      this._page = 1
      this._hasMore = true
      this._hasUserScrolled = false
      this.zones.set([])
      this._savedSnapshot.set(new Map())
      this.zoneErrors.set(new Map())
    }
    this.isLoading.set(true)
  }

  /**
   * Called when a page of zones is loaded.
   * @param {Zone[]} pageData - The page of zones to load.
   */
  private _onPageLoaded(pageData: Zone[]): void {
    this._mergePageFromServer(pageData)
    this._hasMore = pageData.length >= this._perPage
    if (this._hasMore) this._page++
    this.isLoading.set(false)
  }

  /**
   * Converts an array of zones to a map by id.
   * @param {Zone[]} zones - The zones to convert to a map.
   * @returns {Map<number, Zone>} - The map of zones by id.
   */
  private _asPersistedMap(zones: Zone[]): Map<number, Zone> {
    const byId = new Map<number, Zone>()
    this._setPersistedMany(byId, zones)
    return byId
  }

  /**
   * Sets many zones in a map.
   * @param {Map<number, Zone>} target - The map to set the zones in.
   * @param {Zone[]} zones - The zones to set in the map.
   * @returns {void}
   */
  private _setPersistedMany(target: Map<number, Zone>, zones: Zone[]): void {
    for (const zone of zones) this._setPersisted(target, zone)
  }

  /**
   * Sets a zone in a map.
   * @param {Map<number, Zone>} target - The map to set the zone in.
   * @param {Zone} zone - The zone to set in the map.
   * @returns {void}
   */
  private _setPersisted(target: Map<number, Zone>, zone: Zone): void {
    if (zone.id != null && zone.id > 0) target.set(zone.id, zone)
  }

  /**
   * Recomputes the weights of the zones.
   * @param {Zone[]} zones - The zones to recompute the weights of.
   * @returns {Zone[]} - The zones with the recomputed weights.
   */
  private _recomputeWeights(zones: Zone[]): Zone[] {
    return zones.map((zone, index) => ({ ...zone, weight: index + 1 }))
  }

  private _getNextWeight(): number {
    let maxWeight = -1
    for (const zone of this.zones()) {
      const weight = Number(zone.weight ?? 0)
      if (!Number.isFinite(weight)) continue
      if (weight > maxWeight) maxWeight = weight
    }
    return maxWeight + 1
  }

  /**
   * Resolves the scroll container for the given element.
   * @param {HTMLElement | null} element - The element to resolve the scroll container for.
   * @returns {HTMLElement | Window} - The scroll container.
   */
  private _resolveScrollContainer(element: HTMLElement | null, includePotentialContainers: boolean): HTMLElement | Window {
    if (!element) return window

    const wizardScrollHost = element.closest('[data-wizard-scroll]') as HTMLElement | null
    if (this._isElementScrollable(wizardScrollHost, includePotentialContainers)) return wizardScrollHost

    // Explicit priority for event wizard vertical scroller.
    const stepContainer = element.closest('.step-container') as HTMLElement | null
    if (this._isElementScrollable(stepContainer, includePotentialContainers)) return stepContainer

    // Fallback: collect all scrollable ancestors and choose the largest viewport host,
    // which is usually the main wizard content scroller.
    const scrollableAncestors: HTMLElement[] = []
    let current = element.parentElement
    while (current) {
      if (this._isElementScrollable(current, includePotentialContainers)) scrollableAncestors.push(current)
      current = current.parentElement
    }

    if (!scrollableAncestors.length) return window
    const selected = scrollableAncestors.reduce((best, candidate) => (candidate.clientHeight > best.clientHeight ? candidate : best))
    return selected
  }

  /**
   * Whether an element is scrollable.
   * @param {HTMLElement | null} element - The element to check.
   * @returns {boolean} - Whether the element is scrollable.
   */
  private _isElementScrollable(element: HTMLElement | null, includePotentialContainers: boolean): element is HTMLElement {
    if (!element) return false
    const tag = element.tagName.toLowerCase()
    if (tag === 'body' || tag === 'html') return false

    const style = getComputedStyle(element)
    const overflowY = style.overflowY
    const hasScrollableOverflow = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay'
    if (!hasScrollableOverflow) return false
    if (includePotentialContainers) return true
    return element.scrollHeight > element.clientHeight
  }

  /**
   * Syncs the drag after auto scroll.
   * @param {number} clientX - The x coordinate of the pointer.
   * @param {number} clientY - The y coordinate of the pointer.
   * @returns {void}
   */
  private _syncDragAfterAutoScroll(clientX: number, clientY: number): void {
    if (this._dragSyncRafId != null) cancelAnimationFrame(this._dragSyncRafId)
    this._dragSyncRafId = requestAnimationFrame(() => {
      const doc = this._activeScrollContainer === window ? document : (this._activeScrollContainer as HTMLElement).ownerDocument
      if (typeof PointerEvent !== 'undefined') {
        doc.dispatchEvent(new PointerEvent('pointermove', { bubbles: true, cancelable: true, clientX, clientY, pointerType: 'mouse' }))
      } else {
        doc.dispatchEvent(new MouseEvent('mousemove', { bubbles: true, cancelable: true, clientX, clientY }))
      }
      this._dragSyncRafId = null
    })
  }

  /**
   * Gets the auto scroll delta.
   * @param {number} pointerY - The y coordinate of the pointer.
   * @returns {number} - The auto scroll delta.
   */
  private _getAutoScrollDelta(pointerY: number): number {
    if (this._activeScrollContainer === window) {
      const viewportHeight = window.innerHeight
      if (pointerY > viewportHeight - this._dragAutoScrollEdgePx) return this._dragAutoScrollStepPx
      if (pointerY < this._dragAutoScrollEdgePx) return -this._dragAutoScrollStepPx
      return 0
    }

    const container = this._activeScrollContainer as HTMLElement
    const rect = container.getBoundingClientRect()
    const topEdge = rect.top + this._dragAutoScrollEdgePx
    const bottomEdge = rect.bottom - this._dragAutoScrollEdgePx
    if (pointerY > bottomEdge) return this._dragAutoScrollStepPx
    if (pointerY < topEdge) return -this._dragAutoScrollStepPx
    return 0
  }

  /**
   * Deletes a key from a map.
   * @param {Map<number, T>} store - The map to delete the key from.
   * @param {number} key - The key to delete.
   * @returns {void}
   */
  private _deleteByKey<T>(store: { update: (fn: (map: Map<number, T>) => Map<number, T>) => void }, key: number): void {
    store.update((map) => {
      if (!map.has(key)) return map
      const next = new Map(map)
      next.delete(key)
      return next
    })
  }

  /**
   * Scrolls the active container.
   * @param {number} delta - The delta to scroll by.
   * @returns {boolean} - Whether the container was scrolled.
   */
  private _scrollActiveContainer(delta: number): boolean {
    if (this._activeScrollContainer === window) {
      const before = window.scrollY
      window.scrollBy({ top: delta, behavior: 'auto' })
      return window.scrollY !== before
    }

    const container = this._activeScrollContainer as HTMLElement
    const before = container.scrollTop
    container.scrollTop += delta
    return container.scrollTop !== before
  }

  private _setGlobalDragCursor(isDragging: boolean): void {
    document.body.style.cursor = isDragging ? 'grabbing' : ''
  }
}
